import React from 'react';
import { Reveal } from '../components/Reveal';
import { Check, X } from 'lucide-react';

/**
 * "Is this for you?" -- marketing ruling M1 (2026-10-20), Phase A.
 *
 * Held back (counsel item M1(5)(c)/(e), Brand Skeptic's dissent): the
 * qualitative index-fund line ("If growth is what you want, a plain index
 * fund is the cheaper, better tool. This portfolio is built to step out of
 * falling markets, which also means it is expected to earn less in rising
 * ones."). It ships, in its Phase B wording, only after counsel clears it.
 * The "$10,000 or less" figure on the last "not for you" bullet is Phase B.
 */
const notForYou = [
  'you want the highest return you can get;',
  'you want an AI to find winning trades;',
  'you want to bring your own strategy or code (we retired that product in October 2026);',
  'you want leverage, shorting, options, futures or currencies;',
  'you need live trading today;',
  'your account is small enough that our fee would be bigger than the falls this is built to shrink.',
];

const mightBeForYou = [
  'you would sell in a 30% fall, and you know it;',
  'you need the money inside ten years;',
  'you run money for other people and need every decision on the record;',
  'you want to see the rules and the limits before anything moves.',
];

export const IsThisForYou: React.FC = () => {
  return (
    <section id="is-this-for-you" className="py-28 px-6 scroll-mt-20">
      <div className="max-w-[1000px] mx-auto">
        <Reveal
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="font-heading text-3xl md:text-[2.75rem] font-bold text-white leading-tight">
            Is this for you?
          </h2>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-6">
          <Reveal
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="p-8 rounded-2xl border border-white/[0.06] bg-white/[0.02]"
          >
            <h3 className="font-heading text-xl font-semibold text-white mb-5">Probably not for you if:</h3>
            <ul className="space-y-3">
              {notForYou.map((line) => (
                <li key={line} className="flex items-start gap-3 text-white/55 text-sm leading-relaxed">
                  <X size={16} className="text-rose-400 mt-0.5 shrink-0" aria-hidden="true" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="p-8 rounded-2xl border border-[#2dd4a0]/20 bg-gradient-to-br from-[#2dd4a0]/10 to-[#2dd4a0]/[0.02]"
          >
            <h3 className="font-heading text-xl font-semibold text-white mb-5">Might be for you if:</h3>
            <ul className="space-y-3">
              {mightBeForYou.map((line) => (
                <li key={line} className="flex items-start gap-3 text-white/55 text-sm leading-relaxed">
                  <Check size={16} className="text-[#2dd4a0] mt-0.5 shrink-0" aria-hidden="true" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
